/**
 * Avatar Service - Centralized MS Graph avatar fetching with caching
 *
 * Two-layer cache:
 *   1. In-memory Map (fast, within same module load)
 *   2. sessionStorage (persistent across route changes and page refreshes)
 *
 * Photos are stored as base64 data URLs so they can be persisted.
 *
 * Usage:
 *   import avatarService from '@/services/avatarService'
 *   const style = await avatarService.getAvatarStyle(user)
 *   const url = await avatarService.getAvatarUrl(user)
 *   await avatarService.preloadAvatars(users)
 */

import graph from './graph'

const STORAGE_PREFIX = 'fw_avatar_'
const NO_PHOTO = '__no_photo__'

// Cache duration: 30 minutes
const CACHE_DURATION_MS = 30 * 60 * 1000

// In-memory cache (fast layer)
// Key: identifier string, Value: { url: string | null, timestamp: number }
const memCache = new Map()

// Pending requests to deduplicate concurrent fetches for the same user
const pendingRequests = new Map()

/**
 * Get the best available graph identifier from a user object
 * Priority: lawyer_url > tenance_email > graph_id > azure_id > email
 */
function getGraphIdentifier(user) {
  if (!user) return null
  return user.lawyer_url || user.tenance_email || user.graph_id || user.azure_id || user.email || null
}

/**
 * Read from sessionStorage layer
 */
function readStorage(identifier) {
  try {
    const raw = sessionStorage.getItem(STORAGE_PREFIX + identifier)
    if (!raw) return null
    const entry = JSON.parse(raw)
    if (Date.now() - entry.ts > CACHE_DURATION_MS) {
      sessionStorage.removeItem(STORAGE_PREFIX + identifier)
      return null
    }
    return entry.url === NO_PHOTO ? null : entry.url
  } catch {
    return null
  }
}

/**
 * Check if an identifier has a valid entry in sessionStorage (including "no photo")
 */
function hasStorageEntry(identifier) {
  try {
    const raw = sessionStorage.getItem(STORAGE_PREFIX + identifier)
    if (!raw) return false
    const entry = JSON.parse(raw)
    return Date.now() - entry.ts <= CACHE_DURATION_MS
  } catch {
    return false
  }
}

/**
 * Write to sessionStorage layer
 */
function writeStorage(identifier, url) {
  try {
    sessionStorage.setItem(STORAGE_PREFIX + identifier, JSON.stringify({
      url: url || NO_PHOTO,
      ts: Date.now()
    }))
  } catch {
    // sessionStorage full or unavailable - silent fail, in-memory cache still works
  }
}

/**
 * Fetch avatar URL from MS Graph with two-layer caching
 */
async function getAvatarUrl(userOrId) {
  const identifier = typeof userOrId === 'string'
    ? userOrId
    : getGraphIdentifier(userOrId)

  if (!identifier) return null

  // Layer 1: in-memory cache
  const mem = memCache.get(identifier)
  if (mem && Date.now() - mem.timestamp < CACHE_DURATION_MS) {
    return mem.url
  }

  // Layer 2: sessionStorage
  if (hasStorageEntry(identifier)) {
    const url = readStorage(identifier)
    // Promote to in-memory cache
    memCache.set(identifier, { url, timestamp: Date.now() })
    return url
  }

  // Deduplicate concurrent requests for the same identifier
  if (pendingRequests.has(identifier)) {
    return pendingRequests.get(identifier)
  }

  const requestPromise = (async () => {
    try {
      // Fetch as base64 data URL so it can be stored in sessionStorage
      const url = await graph.getUserPhoto(identifier, { format: 'dataUrl' })

      memCache.set(identifier, { url, timestamp: Date.now() })
      writeStorage(identifier, url)

      return url
    } catch (error) {
      console.warn('[AvatarService] Failed to fetch avatar for', identifier)

      memCache.set(identifier, { url: null, timestamp: Date.now() })
      writeStorage(identifier, null)

      return null
    } finally {
      pendingRequests.delete(identifier)
    }
  })()

  pendingRequests.set(identifier, requestPromise)
  return requestPromise
}

/**
 * Get avatar as a Vue style object for :style binding
 */
async function getAvatarStyle(userOrId) {
  const url = await getAvatarUrl(userOrId)
  return url ? { backgroundImage: `url(${url})` } : null
}

/**
 * Preload avatars for multiple users
 */
async function preloadAvatars(users, identifierKey = null) {
  if (!users || !Array.isArray(users)) return new Map()

  const results = new Map()

  await Promise.all(users.map(async (user) => {
    const userObj = identifierKey ? user[identifierKey] : user
    const identifier = getGraphIdentifier(userObj)
    if (!identifier) return
    const url = await getAvatarUrl(userObj)
    results.set(identifier, url)
  }))

  return results
}

/**
 * Enrich user objects with avatar_style property
 */
async function enrichWithAvatars(users, userKey = null) {
  if (!users || !Array.isArray(users)) return

  await Promise.all(users.map(async (item) => {
    const userObj = userKey ? item[userKey] : item
    item.avatar_style = await getAvatarStyle(userObj)
  }))
}

/**
 * Clear both cache layers
 */
function clearCache() {
  memCache.clear()
  pendingRequests.clear()
  try {
    const keysToRemove = []
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i)
      if (key && key.startsWith(STORAGE_PREFIX)) {
        keysToRemove.push(key)
      }
    }
    keysToRemove.forEach(function(key) { sessionStorage.removeItem(key) })
  } catch {
    // ignore
  }
}

/**
 * Get user initials for fallback display
 */
function getInitials(user) {
  if (!user) return '??'

  if (user.ime && user.prezime) {
    return (user.ime[0] + user.prezime[0]).toUpperCase()
  }

  if (user.name) {
    const parts = user.name.split(' ')
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase()
    }
    return user.name.substring(0, 2).toUpperCase()
  }

  if (user.displayName) {
    const parts = user.displayName.split(' ')
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase()
    }
    return user.displayName.substring(0, 2).toUpperCase()
  }

  return '??'
}

export default {
  getAvatarUrl,
  getAvatarStyle,
  preloadAvatars,
  enrichWithAvatars,
  clearCache,
  getInitials,
  getGraphIdentifier
}
